import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyTooltip as MatTooltip } from '@angular/material/legacy-tooltip';
import { Chart, ChartItem, registerables } from 'chart.js';
import 'chartjs-adapter-luxon';
import ChartStreaming from 'chartjs-plugin-streaming';
import { MILL_SECONDS } from 'common/constant/general';
import { Strings } from 'common/utility/string/string';
import { Subject, debounceTime, filter, fromEvent, takeUntil } from 'rxjs';
import { NodeTypes } from "services/definition/class/node-types";
import { DefinitionService } from 'services/definition/definition.service';
import { TreeNodeType } from "services/definition/enum";
import { GetDataRequestStatus, QueryRequest, SORT_ORDER } from 'services/get-data/get-data-common';
import { GetDataService } from 'services/get-data/get-data.service';
import { StorageService } from 'services/storage/storage.service';
import { TokenService } from 'services/token/token.service';
import { Logger } from 'src/app/common/utility/logger/logger';
import { CountryCode } from 'src/app/services/translation/country-code';
import { TranslationService } from 'src/app/services/translation/translation.service';
import { Environment } from 'src/environments/environment';
import { FUNC_TYPE, LineSubscription, LineWidgetSettings, TIME_HORIZON, TimeHorizon, Y_AXIS_POSITION } from './line-widget-common';
import { LINEWIDGET_DEFAULT, LineWidgetSettingsComponent } from './line-widget-settings/line-widget-settings.component';
import { DEFAULT_DELAY, WorkerMessage, WorkerRequest, WorkerResult } from './line-widget-worker/line-widget-worker-interface';

import { DisplayGrid } from 'angular-gridster2';
import zoomPlugin from 'chartjs-plugin-zoom';
import { scaleLegendClick } from '../plugin/scale-legend';

Chart.register(...registerables)
Chart.register(zoomPlugin, ChartStreaming)
Chart.register(scaleLegendClick)

// Constants
const MAX_PROGRESS_TIMEOUT = 60 // 60 Seconds

@Component({
  selector: 'line-widget',
  templateUrl: './line-widget.component.html',
  styleUrls: ['./line-widget.component.scss']
})
export class LineWidgetComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('canvas', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;
  @Input() lineSettings: LineWidgetSettings = LINEWIDGET_DEFAULT
  @Output() lineSettingsChange = new EventEmitter<LineWidgetSettings>
  @Input() editMode!: boolean
  @Output() deleteWidgetChange = new EventEmitter<boolean>()

  private unsubscribe: Subject<void> = new Subject()
  private chartId: string | undefined
  private chart: Chart | undefined
  private lineWidgetDatasets: Array<any> = []
  private chartOptionSettings: any = {}
  private languageCode: CountryCode | undefined = undefined
  private lineSubscriptions: Array<LineSubscription> = []
  private preloadRequest: QueryRequest = { requests: [], startTime: 0, endTime: 0, sort: SORT_ORDER.NONE }
  private worker: Worker | undefined
  private loading: boolean = false
  private loadingId: number = 0
  private loadingTimeout: number = 0
  public titleStyle!: string

  // Localizing the TimeHorizon selection
  private horizon: Array<TimeHorizon> = [
    { id: TIME_HORIZON.MINUTES, local: 'widgets.line-widget.minutes', label: '' },
    { id: TIME_HORIZON.HOURS, local: 'widgets.line-widget.hours', label: '' },
    { id: TIME_HORIZON.DAYS, local: 'widgets.line-widget.days', label: '' },
    { id: TIME_HORIZON.WEEKS, local: 'widgets.line-widget.weeks', label: '' }
  ];

  constructor(
    private dialog: MatDialog,
    private translationService: TranslationService,
    private getDataService: GetDataService,
  ) {
    this.listenForGetDataUpdates();
    this.startWebWorker()
  }

  @ViewChild("zoomTooltip") zoomTooltip!: MatTooltip

  public showTooltip(): void {
    this.zoomTooltip.disabled = false
    this.zoomTooltip.show()
    setTimeout(() => {
      this.zoomTooltip.disabled = true
    }, 2000)
  }

  public get showHelpIcon(): boolean {
    return !this.isIosSafari
  }

  private isIosSafari: boolean = false

  public chartClicked(sourceInfo: any, context: any): void {
    const chart = context._context.chart

    if (!!chart) {
      let show
      Object.keys(chart.scales).forEach((scale: any) => {
        const scaleOptions = chart.options.scales[scale]
        show = !scaleOptions.grid.display
        chart.options.scale.ticks.display
        if (!!scaleOptions) {
          LineWidgetComponent.setNestedValue(scaleOptions, ['grid', 'display'], show)
        }
      })
      chart.options.scales.x.grid.display = show
      chart.update()
    }
  }

  private static setNestedValue(target: any, propertyPath: Array<string>, value: any): any {
    const returnValue = target
    if (!returnValue) {
      return
    }

    if (propertyPath.length === 1) {
      target[propertyPath[0]] = value
      return
    }

    return LineWidgetComponent.setNestedValue(target[propertyPath[0]], propertyPath.slice(1), value)
  }

  public ngOnInit(): void {
    // TBD Probably need to add other touch devices?
    const isIos = navigator.userAgent.indexOf("iPad") ||
      navigator.userAgent.indexOf("iPhone") ||
      navigator.userAgent.indexOf("iPod")

    // So, is it iOS?
    if (isIos == -1)
      this.isIosSafari = false
    else
      this.isIosSafari = true

    this.loadTranslations();

    fromEvent(window, 'resize').pipe(debounceTime(500))
      .subscribe(() => {
        if (this.worker) {
          this.worker.postMessage(this.createWorkerMessage(WorkerRequest.Resized, WorkerResult.Success,
            {
              width: window.innerWidth
            }
          ));
        }
      })

    this.titleStyle = "display: flex; flex-direction: column;" +
      "font-weight: " + this.lineSettings.title.font.weight + ";" +
      "align-items: " + this.lineSettings.title.align + ";" +
      "font-size: " + this.lineSettings.title.font.size + "px;"

    // Init the basic chart settings
    this.setChartOptions(this.lineSettings);

    // Build the Line Chart lines
    this.lineSubscriptions = [];
    this.preloadRequest.requests = [];
    this.buildSubscriptionsRequest(<any>this.lineSettings.sourceNodes);
    this.configureLineWidget(this.lineSubscriptions);

    this.createChart()
  } // ngOnInit()

  public ngOnDestroy(): void {
    // Does the disconnect actually occur?
    if (this.worker) {
      this.worker.postMessage(this.createWorkerMessage(WorkerRequest.Disconnect, WorkerResult.Success, {}))
      this.worker.terminate();
    }

    // If there is a previous transaction
    if (this.currentTransactionId) {
      this.stopLoadingProgress()
      this.getDataService.cancelGetData(this.currentTransactionId);
    }

    if (this.chart) {
      this.chart.destroy()
      this.chart = undefined
      this.chartId = undefined
    }

    this.unsubscribe.next();
    this.unsubscribe.complete();
  } // ngOnDestroy()

  public ngOnChanges(changes: SimpleChanges): void {
    const lineSettings = 'lineSettings'
    const editMode = 'editMode'

    if (!!changes[lineSettings]) {
      const change = changes[lineSettings]
      // If previous isn't undefined
      if (change.isFirstChange() || (change.previousValue !== change.currentValue)) {
        // Init the basic chart settings
        this.setChartOptions(this.lineSettings)

        // Build the Line Chart lines
        this.lineSubscriptions = []
        this.preloadRequest.requests = []
        this.buildSubscriptionsRequest(<any>this.lineSettings.sourceNodes)
        this.configureLineWidget(this.lineSubscriptions)

        this.createChart()

        this.startWorkerSubscription()
      } // If previous!= current
    }
  } // ngOnChanges()

  private createChart(): void {
    if (this.chart) {
      this.chart.destroy()
    }

    // Create the chart
    this.chartId = Strings.newGuid()
    this.canvas.nativeElement.setAttribute('id', this.chartId)
    const ctx = document.getElementById(this.chartId);

    if (ctx) {
      this.chart = new Chart(<ChartItem>ctx, {
        type: 'line',
        data: {
          datasets: this.lineWidgetDatasets
        },
        options: this.chartOptionSettings
      });
    }

  } // createChart()

  private createWorkerMessage(request: WorkerRequest, result: WorkerResult | undefined, data: any | undefined): WorkerMessage {
    return {
      request: request,
      result: result,
      data: data
    }
  } // createWorkerMessage()

  private startWebWorker(): void {
    if (typeof Worker !== 'undefined') {
      // Create Worker
      this.worker = new Worker(new URL('./line-widget-worker/line-widget.worker', import.meta.url));
      this.worker.onmessage = (event) => this.onWorkerMessage(event);
      this.worker.onerror = (error) => this.onWorkerError(error);

      // Start the WebSocket Connection
      const tokenService: TokenService = new TokenService();
      const connect: any = {
        url: Environment.SocketUrl,
        poolId: StorageService.PoolId,
        accessToken: StorageService.AccessToken,
        authId: tokenService.AuthenticationId
      }
      this.worker.postMessage(this.createWorkerMessage(WorkerRequest.Connect, WorkerResult.Success, connect))
    } else {
      // TODO: Get Browser/OS Information to include
      Logger.error("Web workers are not supported in this environment.")
    }
  } // startWebWorker()

  // Process Messages from Worker Thread
  private onWorkerMessage(event: any): void {
    if (!event || !event.data) {
      return
    }

    const message: WorkerMessage = event.data;
    switch (message.request) {
      case WorkerRequest.Disconnect: // Worker Disconnected
        if (this.worker) {
          this.worker.terminate()
        }
        break;
      case WorkerRequest.PreLoadData:
        try {
          this.preloadData();
        } catch (error) {
          Logger.error(`WorkerRequest.PreLoadData Error: ${JSON.stringify(event)} ${error}`)
        }
        break;
      case WorkerRequest.DataUpdated:
        try {
          if (this.play) {
            this.processDataUpdated(message);
          }
        } catch (error) {
          Logger.error(`WorkerRequest.DataUpdated Error: ${JSON.stringify(event)} ${error}`)
        }
        break;
      case WorkerRequest.Error:
        Logger.error(`WorkerRequest.Error ${JSON.stringify(event)}`)
        if (this.worker) {
          this.worker.terminate();
        }
        break;
      default:
        Logger.error("Unknown Worker Request")
        if (this.worker) {
          this.worker.terminate();
        }
    }
  } // onWorkerMessage

  private onWorkerError(error: any): void {
    Logger.error(`onWorkerError: ${error}`)
  }

  private processDataUpdated(msg: any): void {
    this.lineWidgetDatasets[msg.data.linePos].data = msg.data.data;
    if (this.chart)
      this.chart?.update('quiet')
  }

  currentTransactionId: string | null = null;
  private listenForGetDataUpdates(): void {
    // Listen for the GetData Service events
    this.getDataService.getDataUpdated.pipe(takeUntil(this.unsubscribe)).subscribe((message) => {
      if (message.transactionId !== this.currentTransactionId)
        return

      // Process my transaction
      switch (message.status) {
        case GetDataRequestStatus.NextPage:
          this.startLoadingProgress()
          Logger.debug(`NextPage ${(Date.now() - this.profileStartTime) / MILL_SECONDS}`)
          this.processGetData(message.data)
          break;
        case GetDataRequestStatus.Complete:
          this.currentTransactionId = null
          Logger.debug(`Complete ${(Date.now() - this.profileStartTime) / MILL_SECONDS}`)
          this.processGetData(message.data)
          this.stopLoadingProgress()
          break;
        case GetDataRequestStatus.Failed:
          this.stopLoadingProgress()
          this.currentTransactionId = null
          Logger.error(`GetData Failed: ${message}`)
          this.stopLoadingProgress()
          break;
      }
    });

  } // listenForGetDataUpdates()

  private processGetData(data: any): void {
    if (this.worker) {
      this.worker.postMessage(this.createWorkerMessage(WorkerRequest.PreLoadData, WorkerResult.Success, data))
    }
    data.splice(0)
  } // processGetData()

  private profileStartTime = 0;
  private preloadData(): void {
    this.profileStartTime = Date.now();

    const startTimeDiff = ((this.lineSettings.timeHorizonDuration
      * (this.getHorizon(this.lineSettings.timeHorizon)) * (60 * MILL_SECONDS)))
      + this.chartOptionSettings.scales.x.realtime.delay

    this.preloadRequest.endTime = new Date().getTime();
    this.preloadRequest.startTime = this.preloadRequest.endTime - startTimeDiff;

    // Request the preload data
    this.currentTransactionId = this.getDataService.getData(this.preloadRequest)
    this.startLoadingProgress()
  } // preloadData()

  private startLoadingProgress(): void {
    this.loading = true
    this.loadingTimeout = MAX_PROGRESS_TIMEOUT

    if (this.loadingId == 0) {
      this.loadingId = window.setInterval(() => {
        if (this.loadingTimeout > 0) {
          this.loadingTimeout--
        } else {
          this.stopLoadingProgress()
        }
      }, MILL_SECONDS) // One second intervals
    }
  } // startLoadingProgress

  private stopLoadingProgress(): void {
    this.loading = false
    if (this.loadingId > 0) {
      window.clearInterval(this.loadingId)
      this.loadingId = 0
    }
  } // stopLoadingProgress

  private startWorkerSubscription(): void {

    const startTimeDiff = ((this.lineSettings.timeHorizonDuration
      * (this.getHorizon(this.lineSettings.timeHorizon)) * (60 * MILL_SECONDS)))
      + this.chartOptionSettings.scales.x.realtime.delay
    const tmp = {
      subscriptions: this.lineSubscriptions,
      horizon: startTimeDiff,
      windowWidth: window.innerWidth
    }
    if (this.worker) {
      this.worker.postMessage(this.createWorkerMessage(WorkerRequest.Subscription, WorkerResult.Success, tmp))
    }

  } // startWorkerSubscription()

  // Takes the subscription and configures the chart
  private configureLineWidget(subscriptions: Array<LineSubscription>) {
    // Initiliaze
    this.chartOptionSettings.scales.x.realtime.delay = DEFAULT_DELAY;
    // this.chartOptionSettings.onClick = this.chartClicked
    this.lineWidgetDatasets.splice(0);
    // For each of the subscriptions
    subscriptions.forEach(line => {
      if (line.settings !== undefined) {
        // Set the ChartDataset for each line
        let key = `${line.sourceId}#${line.deviceId}#${line.lineId}`
        this.lineWidgetDatasets.push(structuredClone({
          label: line.displayName,
          backgroundColor: line.settings.lineColor,
          borderColor: line.settings.lineColor,
          cubicInterpolationMode: 'monotone',
          fill: false,
          // tension: 0.5,
          yAxisID: key,
          pointHitRadius: 20,
          pointHoverRadius: 20,
          pointRadius: 0, // Hides the point, default 3
          pointBorderColor: line.settings.lineColor,
          pointBackgroundColor: line.settings.lineColor,
          data: []
        })) // push lineWidgetDataset

        // Setup the Line Y Axis
        this.chartOptionSettings['scales'][key] = {
          title: {
            display: line.settings.showTitle,
            text: line.displayName,
            color: line.settings.lineColor,
            font: {
              weight: 'bolder'
            }
          },
          type: 'linear',
          display: (line.settings.yAxisPosition == Y_AXIS_POSITION.NONE) ? false : true,
          position: (line.settings.yAxisPosition == Y_AXIS_POSITION.LEFT) ? Y_AXIS_POSITION.LEFT : Y_AXIS_POSITION.RIGHT,
          min: line.settings.minYaxis,
          max: line.settings.maxYaxis
        } // Setup the Line Y Axis

        // Setup the Realtime X Axis
        if ((line.settings.function != FUNC_TYPE.REALTIME)
          && ((line.settings.functionTimeframe + DEFAULT_DELAY) > this.chartOptionSettings.scales.x.realtime.delay)) {
          this.chartOptionSettings.scales.x.realtime.delay = DEFAULT_DELAY + (line.settings.functionTimeframe * MILL_SECONDS);
        }
      } // If Line.Settings valid
    }) // forEach Subscription
  } // configureLineWidget()

  // Get the translated Horizonlabels (minutes, hours, ...)
  private getCurrentHorizon(horizon: string): string | undefined {
    for (let x = 0; x < this.horizon.length; x++) {
      if (this.horizon[x].id === this.lineSettings.timeHorizon) {
        return this.horizon[x].label;
      }
    }
    return undefined;
  } // getCurrent()

  public get isLoading(): boolean {
    return this.loading
  } // get isLoading()

  // Determine time horizon calculation for chart
  private getHorizon(horizon: string): number {
    switch (horizon) {
      case TIME_HORIZON.WEEKS: // Day is 24 hours
        return 7 * 24 * 60;
      case TIME_HORIZON.DAYS: // Day is 24 hours
        return 24 * 60;
      case TIME_HORIZON.HOURS: // Hour is 60 minutes
        return 60;
      case TIME_HORIZON.MINUTES: // Minutes/default
      default:
        return 1;
    }
  } // getHorizon()

  private setChartOptions(settings: LineWidgetSettings) {
    this.chartOptionSettings = {
      scales: {
        y: {
          display: false
        },
      },
      responsive: true,
      displayGrid: DisplayGrid.None,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: settings.showLegend,
          position: 'top'
        },
        zoom: {
          pan: {
            enabled: true,
            modifierKey: 'ctrl',
            onPanComplete: (chart: any) => this.completedZoomPan(chart),
          },
          zoom: {
            wheel: {
              enabled: true
            },
            pinch: {
              enabled: true
            },
            mode: 'xy',
            onZoomComplete: (chart: any) => this.completedZoomPan(chart),
          },
        },
        scaleLegendClick: {
          onClick: this.chartClicked
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: true
      },
      parsing: false,
      normalized: true,
      animation: false,
      spanGaps: true
    } // chartOptionSettings

    this.chartOptionSettings['scales'] = {}
    this.chartOptionSettings['scales']['y'] = {
      display: false,
      grid: {
        display: false
      },
    }
    this.chartOptionSettings['scales']['x'] = {
      grid: {
        display: false
      },
      title: {
        display: true,
        text: `${this.labelLast} ${settings.timeHorizonDuration} ${this.getCurrentHorizon(settings.timeHorizon)}`,
      },
      type: 'realtime',
      realtime: {
        duration: (settings.timeHorizonDuration * this.getHorizon(settings.timeHorizon)) * (60 * MILL_SECONDS),
        delay: DEFAULT_DELAY, // default
        refresh: MILL_SECONDS, // Once a Second
        onRefresh: (chart: Chart) => this.realTimeOnRefresh(chart)
      },
      time: {
        tooltipFormat: 'yyyy-MM-dd HH:mm:ss',
        displayFormats: {
          second: "HH:mm:ss",
          minute: "HH:mm",
          hour: "MM-dd HH:mm",
          day: 'MM-dd',
          month: "yyyy-MM"
        }
      },
      ticks: {
        source: 'auto',
        autoSkip: true,
        maxTicksLimit: 10
      }
    } // chartOptionSettings['scales']['x']
  } // setChartOptions()

  // Does nothing for now, Web Worker does it
  private realTimeOnRefresh(chart: Chart): void {
    // chart.render()
  } // realTimeOnRefresh()

  private completedZoomPan(chart: any): void {
    this.resetZoomDisabled = false;
  } // zoomComplete()

  public resetZoomDisabled: boolean = true

  public resetZoomClicked(): void {
    if (this.chart) {
      this.chart.resetZoom()
    }
    this.resetZoomDisabled = true
  } // resetZoom()


  private arraySignalKey: Array<string> = [];
  private sourceId!: string;
  private sourceDisplayName!: string;
  private deviceId!: string;
  private deviceDisplayName!: string;

  private buildSubscriptionsRequest(nodes: Array<any>): void {

    nodes.forEach((node) => {
      switch (NodeTypes.getNodeType(node)) {
        case TreeNodeType.Source:
          this.arraySignalKey = []
          this.sourceId = node.name;
          this.sourceDisplayName = node.displayName

          if (node.children) { // If no, then mis-configured tree
            this.buildSubscriptionsRequest(node.children);
          }
          break;
        case TreeNodeType.ArrayNode:
          if (node.children) {
            this.buildSubscriptionsRequest(node.children);
          }
          break;
        case TreeNodeType.ObjectNode:
          this.arraySignalKey.push(node.name)
          if (node.children) {
            this.buildSubscriptionsRequest(node.children);
            this.arraySignalKey.pop()
          }
          break;
        case TreeNodeType.Group:
          if (node.children) {
            this.buildSubscriptionsRequest(node.children);
          }
          break;
        case TreeNodeType.Device:
          this.arraySignalKey = []
          this.deviceId = node.name
          this.deviceDisplayName = node.displayName

          if (node.children) {
            this.buildSubscriptionsRequest(node.children);
          }
          break;
        case TreeNodeType.Telemetry:
          const key = (this.arraySignalKey.length ? (this.arraySignalKey.join("")) : '') + node.name
          this.appendQueryRequest(this.sourceId + "#" + this.deviceId, key)

          let displayName = ""
          if (node.settings.showSourceName) {
            displayName = this.sourceDisplayName + "->"
          }
          if (node.settings.showDeviceName) {
            displayName += this.deviceDisplayName + "->"
          }

          // node.settings.customName = "aaa"
          displayName += node.settings.showCustomName ? node.settings.customName : node.displayName

          if (node.settings.showUoMSymbol) {
            const uomSymbol = DefinitionService.getUoMSymbol(node.ltmiRef.semanticType, node.ltmiRef.unit)
            if (uomSymbol) {
              displayName += "(" + uomSymbol + ")"
            }
          }

          const subscription: LineSubscription = {
            sourceId: this.sourceId,
            deviceId: this.deviceId,
            displayName: displayName,
            ltmi: node.ltmi,
            lineId: key,
            settings: node.settings,
            bulkData: [],
            data: [],
            _data: []
          }
          this.lineSubscriptions.push(subscription)
          break;
        default: // Something is wrong
          Logger.error("LineWidgetSettings-getSubscriptions() - unknown type: ", node)
          break;
      }
    });
  } // buildSubscriptions()

  private appendQueryRequest(primaryKey: string, signal: string) {
    // If this contains arrays
    const arrayIndex = signal.lastIndexOf("]")
    if (arrayIndex >= 0) {
      // Is this an Array of signals?
      if (arrayIndex == signal.length - 1) {
        const subIndex = signal.lastIndexOf("]", arrayIndex - 1)
        // Is this an array of?
        if (subIndex > 0) {
          primaryKey = primaryKey + '#' + signal.substring(0, subIndex + 1)
          signal = signal.substring(subIndex + 1)
        }
      } else { // Not SubArrays
        primaryKey = primaryKey + '#' + signal.substring(0, arrayIndex + 1)
        signal = signal.substring(arrayIndex + 1)
      }
    }

    // If Existing Query, Update
    let found = this.preloadRequest.requests.find(request => (request.key === primaryKey))
    if (found) {
      found.signals.push(signal)
      return
    }

    // New Query
    this.preloadRequest.requests.push({
      key: primaryKey,
      signals: [signal]
    })
  } // appendQueryRequest()

  // Translation code
  private labelLast: string | undefined;
  private loadTranslations(): void {
    const label_last: string = 'widgets.line-widget.last';
    const labels: Array<string> = [
      label_last
    ];
    for (let x = 0; x < this.horizon.length; x++) {
      labels.push(this.horizon[x].local);
    }

    this.translationService.get(labels)
      .pipe(
        takeUntil(this.unsubscribe)
      ).subscribe(translations => {
        this.labelLast = translations[label_last];
        // Get array of horizon labels, starts at 1 because of label_last
        for (let x = 0; x < this.horizon.length; x++) {
          this.horizon[x].label = translations[labels[x + 1]];
        }
      });
  } // loadTranslations()

  private watchLanguageChanges(): void {
    this.translationService.LanguageChange.pipe(
      takeUntil(this.unsubscribe),
      filter((languageCode: CountryCode) => {
        Logger.debug(`Language Filter CountryCode: ${languageCode}.Current: '${this.languageCode}'`)
        return languageCode !== this.languageCode
      })
    ).subscribe((languageCode: CountryCode) => {
      this.languageCode = languageCode
      this.loadTranslations();
    })
  } // watchLanguageChanges()

  public changeSettings(): void {
    const settingsDialog = this.dialog.open(LineWidgetSettingsComponent, { disableClose: true, data: { settings: this.lineSettings } });

    // Subscribe to the result
    settingsDialog.afterClosed().subscribe((res) => {
      if (res && res.event === 'apply') {
        this.lineSettings = structuredClone(res.data.settings)

        this.lineSettingsChange.emit(this.lineSettings)

        this.titleStyle = "display: flex; flex-direction: column; " +
          "font-weight: " + this.lineSettings.title.font.weight + ";" +
          "align-items: " + this.lineSettings.title.align + ";" +
          "font-size: " + this.lineSettings.title.font.size + "px;"

        this.setChartOptions(this.lineSettings)

        // Build the Line Chart lines
        this.lineSubscriptions = []
        this.preloadRequest.requests = []
        this.buildSubscriptionsRequest(<any>this.lineSettings.sourceNodes)
        this.configureLineWidget(this.lineSubscriptions)

        this.createChart()

        // this.startWorkerSubscription()
      }
    });
  } // changeSettings()

  public deleteWidget(): void {
    this.deleteWidgetChange.emit(true)
  } // deleteWidget()

  public getTitle(): string {
    return this.lineSettings.title.showTitle ? this.lineSettings.title.text : ""
  } // getTitle()

  private play: boolean = true
  public clickPlayPause(): void {
    this.play = !this.play
    if (this.chart && this.chart.options && this.chart.options.plugins) {
      this.chart.options.plugins.streaming!.pause = !this.play
      this.chart.update()
    }
  } // clickPlayPause()

  public iconPlayPause(): string {
    return this.play ? "pause" : "play_arrow"
  } // iconPlayPause()

} // class LineWidgetComponent
